import React, { useRef, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import Form, { FormInputComponent } from "../../components/form/Form";
import { Alert, Checkbox, Radio, Space, Modal as ANTModal, Button } from "antd";
import { useMutation } from '@tanstack/react-query';
// import LoadingSpinner from "../../components/LoadingSpinner";
import { useProject } from "../../providers/ClientProjectProvider";

const CANCEL_REASONS = [
    'Client cancelled the project',
    'Client filled position',
    'Other'
]

const CancelProjectModal = ({ project, open: openProp, ...props }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isModalOpen = openProp !== undefined
        ? !!openProp
        : searchParams.has("closeProject");

    const handleModalClose = () => {
        if (openProp === undefined && searchParams.has("closeProject")) {
            searchParams.delete('closeProject');
            setSearchParams(searchParams);
        }
        props.onToggle?.();
    }

    return (
        <ANTModal
            open={isModalOpen}
            onCancel={handleModalClose}
            footer={false}
            title={project ? (<>Cancel Project: <b>{project.name}</b></>) : 'Cancel Project'}
            destroyOnClose={true}
        >
            {project && (
                <CancelProjectForm
                    project={project}
                    onClose={handleModalClose}
                />
            )}
        </ANTModal>
    )
};

const CancelProjectForm = ({ project, onClose }) => {
    const cancelProjectForm = useRef(null);
    const canEnableEmails = project.enable_emails;

    const [cancelFormModel, setCancelFormModel] = useState({
        enable_emails: project.enable_emails,
        talent_emails_enabled: canEnableEmails ? project.talent_emails_enabled : false,
        client_emails_enabled: canEnableEmails ? project.client_emails_enabled : false,
        cancel_reason: undefined
    });
    const cancelProjectMutation = useMutation(['clientLeadCancelProject'], async (params) => {
        await TB.wait(500);
        return project.cancelByClientLead(params)
    })

    const onSubmit = async (model) => {
        let params = {
            ...model,
            enable_emails: (model.talent_emails_enabled || model.client_emails_enabled) ? 1 : 0,
            talent_emails_enabled: model.talent_emails_enabled ? 1 : 0,
            client_emails_enabled: model.client_emails_enabled ? 1 : 0
        }

        let response = await cancelProjectMutation.mutateAsync(params).catch(console.warn);

        if (response) {
            TB.toast('Project closed successfully', { theme: 'warning' })
            return onClose();
        } else {
            TB.showServerErrorToast();
        }
    }

    const onFormChange = (model) => {
        let updates = { ...cancelFormModel };
        for (let [key, value] of Object.entries(model)) {
            updates = TB.set(updates, key, value)
        }
        if (!updates.enable_emails) {
            updates.talent_emails_enabled = false;
            updates.client_emails_enabled = false;
        }

        setCancelFormModel(updates);
    }

    return (
            <div className="relative">
                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message={(
                        <strong style={{ color: TB.config('color.warning-darker') }}>
                            Project will be cancelled and removed from job board
                        </strong>
                    )}
                />

                <Form
                    ref={cancelProjectForm}
                    showRequired
                    minimal
                    background="transparent"
                    title={null}
                    subtitle={null}
                    submitButton={false}
                    onSubmit={onSubmit}
                    model={cancelFormModel}
                    onChange={onFormChange}
                    isDisabled={cancelProjectMutation.isLoading}
                    fields={[]}
                >
                    <div className="flex flex-col items-stretch gap-[24px] transition-all">
                        <div className="flex flex-col justify-between items-stretch gap-[16px] mt-2">

                            <div className="flex-grow flex flex-col items-stretch gap-[8px]">
                                <label className="col-form-label font-semibold whitespace-nowrap">
                                    Set Notification Settings
                                    {!canEnableEmails && (
                                        <small className="text-danger font-bold italic ml-2">
                                            (Project Emails Disabled)
                                        </small>
                                    )}
                                </label>
                                <div className="flex flex-col items-stretch">
                                    <FormInputComponent
                                        type="antd-input"
                                        label={false}
                                        fieldName="talent_emails_enabled"
                                        disabled={cancelProjectMutation.isLoading || !canEnableEmails}
                                        value={cancelFormModel.talent_emails_enabled}
                                    >
                                        <Checkbox checked={cancelFormModel.talent_emails_enabled}>
                                            Notify interested, shortlisted, and/or selected talent
                                        </Checkbox>
                                    </FormInputComponent>

                                    <div className={TB.mapClass("select-none mt-[-0.5rem] mb-6 self-center", {
                                        "opacity-70": cancelProjectMutation.isLoading
                                    })}>
                                        <small>
                                            <b>Total Interested and/or Shortlisted Candidates: <span className="text-secondary">{project.candidate_proposals_received_count}</span></b>
                                        </small>
                                    </div>

                                    <FormInputComponent
                                        type="antd-input"
                                        label={false}
                                        disabled={cancelProjectMutation.isLoading || !canEnableEmails}
                                        fieldName="client_emails_enabled"
                                        value={cancelFormModel.client_emails_enabled}
                                    >
                                        <Checkbox
                                            checked={cancelFormModel.client_emails_enabled}
                                            disabled={cancelProjectMutation.isLoading || !canEnableEmails}
                                        >
                                            Notify client owners and collaborators
                                        </Checkbox>
                                    </FormInputComponent>

                                    <div className={TB.mapClass("select-none mt-[-0.5rem] mb-6 self-center", {
                                        "opacity-70": cancelProjectMutation.isLoading
                                    })}>
                                        <small>
                                            <b>Total Client Owners/Collaborators: <span className="text-secondary">{project.assigned_users_count}</span></b>
                                            {project.assigned_users?.length && (
                                                <ul className="list-disc">
                                                    {TB.map(project.assigned_users, user => (
                                                        <li key={`${user.first_name} ${user.last_name}`}>
                                                            {`${user.first_name} ${user.last_name}`}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col justify-start gap-[8px] min-h-[23px] items-start">
                                <div className="relative">
                                    <FormInputComponent
                                        type="antd-input"
                                        label={<b>Select Cancel Reason</b>}
                                        required={true}
                                        fieldName="cancel_reason"
                                        disabled={cancelProjectMutation.isLoading}
                                        value={cancelFormModel.cancel_reason}
                                    >
                                        <Radio.Group value={cancelFormModel.cancel_reason}>
                                            <Space direction="vertical">
                                                {CANCEL_REASONS.map(reason => (
                                                    <Radio
                                                        value={reason}
                                                        key={reason}
                                                        disabled={cancelProjectMutation.isLoading}
                                                    >
                                                        {reason}
                                                    </Radio>
                                                ))}
                                            </Space>
                                        </Radio.Group>
                                    </FormInputComponent>
                                </div>
                            </div>
                            <div className="flex flex-row self-stretch justify-end items-center gap-3">
                                {/* <button
                                    type="button"
                                    onClick={handleModalClose}
                                    className="btn btn-dark text-sm"
                                    disabled={cancelProjectMutation.isLoading}
                                >
                                    Close
                                </button> */}
                                <Button
                                    htmlType="button"
                                    onClick={onClose}
                                    className="btn btn-dark text-sm h-[unset] [&>.ant-btn-loading-icon]:hidden"
                                    loading={cancelProjectMutation.isLoading}
                                >
                                    Close
                                </Button>
                                <Button
                                    htmlType="submit"
                                    className="btn btn-primary text-sm h-[unset]"
                                    // disabled={cancelProjectMutation.isLoading}
                                    loading={cancelProjectMutation.isLoading}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>

                </Form>
            </div>
    )
};

export default CancelProjectModal;

const CancelProjectWrapper = (props) => {
    const { auth } = useAuth();
    const { project, isLoading } = useProject();

    if (isLoading || !project || !auth?.canManageClients() || !project.can_be_cancelled) {
        return null;
    }

    return (
        <CancelProjectModal project={project} />
    )
}

export { CancelProjectWrapper }